import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';

import Box from '../box/box.jsx';
import DOMElementRenderer from '../../containers/dom-element-renderer.jsx';
import Loupe from '../loupe/loupe.jsx';
import MonitorList from '../../containers/monitor-list.jsx';
import Stage3D from '../stage-3d/stage-3d.jsx';
import TargetHighlight from '../../containers/target-highlight.jsx';
import GreenFlagOverlay from '../../containers/green-flag-overlay.jsx';
import Question from '../../containers/question.jsx';
import MicIndicator from '../mic-indicator/mic-indicator.jsx';
import {STAGE_DISPLAY_SIZES} from '../../lib/layout-constants.js';
import {STAGE_VIEW_MODES} from '../../reducers/stage-view-mode';
import {getStageDimensions} from '../../lib/screen-utils.js';
import styles from './stage.css';

const STAGE_3D_MAX_WIDTH = 1400;
const STAGE_3D_SIDEBAR_WIDTH = 384;
const STAGE_3D_HORIZONTAL_CHROME = 56;
const STAGE_3D_VERTICAL_CHROME = 132;
const STAGE_3D_PREVIEW_VERTICAL_CHROME = 92;

const StageComponent = props => {
    const {
        canvas,
        dragRef,
        isColorPicking,
        isFullScreen,
        isStarted,
        colorInfo,
        micIndicator,
        question,
        stage3DWorkspaceMode,
        stageSize,
        stageViewMode,
        useEditorDragStyle,
        vm,
        onDeactivateColorPicker,
        onDoubleClick,
        onSet3DWorkspaceMode,
        onQuestionAnswered,
        ...boxProps
    } = props;

    const is3DView = stageViewMode === STAGE_VIEW_MODES.threeD;
    const stageDimensions = getStageDimensions(stageSize, isFullScreen);
    if (is3DView && !isFullScreen) {
        const sidebarWidth = stage3DWorkspaceMode === 'preview' ? 0 : STAGE_3D_SIDEBAR_WIDTH;
        const verticalChrome = stage3DWorkspaceMode === 'preview' ?
            STAGE_3D_PREVIEW_VERTICAL_CHROME :
            STAGE_3D_VERTICAL_CHROME;
        const preferredWidth = window.innerWidth - sidebarWidth - STAGE_3D_HORIZONTAL_CHROME;
        const availableHeight = Math.max(
            stageDimensions.height,
            window.innerHeight - verticalChrome
        );
        const width = Math.max(stageDimensions.width, Math.min(preferredWidth, STAGE_3D_MAX_WIDTH));
        stageDimensions.width = width;
        stageDimensions.height = availableHeight;
        stageDimensions.scale = width / stageDimensions.widthDefault;
    }

    return (
        <React.Fragment>
            <Box
                className={classNames(
                    styles.stageWrapper,
                    {[styles.withColorPicker]: !isFullScreen && isColorPicking})}
                onDoubleClick={onDoubleClick}
            >
                <Box
                    className={classNames(
                        styles.stage,
                        {[styles.fullScreen]: isFullScreen}
                    )}
                    style={{
                        height: stageDimensions.height,
                        width: stageDimensions.width
                    }}
                >
                    <DOMElementRenderer
                        domElement={canvas}
                        style={{
                            height: stageDimensions.height,
                            opacity: is3DView ? 0 : 1,
                            pointerEvents: is3DView ? 'none' : 'auto',
                            width: stageDimensions.width
                        }}
                        {...boxProps}
                    />
                    <Stage3D
                        onSet3DWorkspaceMode={onSet3DWorkspaceMode}
                        stage3DWorkspaceMode={stage3DWorkspaceMode}
                        stageViewMode={stageViewMode}
                        vm={vm}
                    />
                    <Box className={styles.monitorWrapper}>
                        <MonitorList
                            draggable={useEditorDragStyle}
                            stageSize={stageDimensions}
                        />
                    </Box>
                    <Box className={styles.frameWrapper}>
                        <TargetHighlight
                            className={styles.frame}
                            stageHeight={stageDimensions.height}
                            stageWidth={stageDimensions.width}
                        />
                    </Box>
                    {isColorPicking && colorInfo ? (
                        <Loupe colorInfo={colorInfo} />
                    ) : null}
                </Box>

                {/* `stageOverlays` is for items that should *not* have their overflow contained within the stage */}
                <Box
                    className={classNames(
                        styles.stageOverlays,
                        {[styles.fullScreen]: isFullScreen}
                    )}
                >
                    <div
                        className={styles.stageBottomWrapper}
                        style={{
                            width: stageDimensions.width,
                            height: stageDimensions.height
                        }}
                    >
                        {micIndicator ? (
                            <MicIndicator
                                className={styles.micIndicator}
                                stageSize={stageDimensions}
                            />
                        ) : null}
                        {question === null ? null : (
                            <div
                                className={styles.questionWrapper}
                                style={{width: stageDimensions.width}}
                            >
                                <Question
                                    question={question}
                                    onQuestionAnswered={onQuestionAnswered}
                                />
                            </div>
                        )}
                    </div>
                    <canvas
                        className={styles.draggingSprite}
                        height={0}
                        ref={dragRef}
                        width={0}
                    />
                </Box>
                {isStarted ? null : (
                    <GreenFlagOverlay
                        className={styles.greenFlagOverlay}
                        wrapperClass={styles.greenFlagOverlayWrapper}
                    />
                )}
            </Box>
            {isColorPicking ? (
                <Box
                    className={styles.colorPickerBackground}
                    onClick={onDeactivateColorPicker}
                />
            ) : null}
        </React.Fragment>
    );
};
StageComponent.propTypes = {
    canvas: PropTypes.instanceOf(Element).isRequired,
    colorInfo: Loupe.propTypes.colorInfo,
    dragRef: PropTypes.func,
    isColorPicking: PropTypes.bool,
    isFullScreen: PropTypes.bool.isRequired,
    isStarted: PropTypes.bool,
    micIndicator: PropTypes.bool,
    onDeactivateColorPicker: PropTypes.func,
    onDoubleClick: PropTypes.func,
    onQuestionAnswered: PropTypes.func,
    onSet3DWorkspaceMode: PropTypes.func,
    question: PropTypes.string,
    stage3DWorkspaceMode: PropTypes.string,
    stageSize: PropTypes.oneOf(Object.keys(STAGE_DISPLAY_SIZES)).isRequired,
    stageViewMode: PropTypes.oneOf(Object.keys(STAGE_VIEW_MODES)).isRequired,
    useEditorDragStyle: PropTypes.bool,
    vm: PropTypes.shape({
        runtime: PropTypes.shape({
            on: PropTypes.func,
            removeListener: PropTypes.func
        })
    }).isRequired
};
StageComponent.defaultProps = {
    dragRef: () => {}
};
export default StageComponent;
